import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

/**
 * ScrapePipelineHealthWidget
 *
 * Renders a timeline of the last N scrape runs with per-stage traffic-light
 * bands (green / yellow / red) based on observed durations. Powered by
 * GET /api/admin/scrape-pipeline-health.
 *
 * Why this exists: a slow stage (e.g. AI summarize phase creeping from 2 min
 * to 5 min) is usually invisible until the feed visibly goes stale. The
 * widget surfaces stage timings at-a-glance so an on-call operator catches
 * regressions before they cause an outage.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const BAND_COLORS = {
  green: { bg: "bg-emerald-500/15", text: "text-emerald-500", dot: "bg-emerald-500" },
  yellow: { bg: "bg-amber-500/15", text: "text-amber-600", dot: "bg-amber-500" },
  red: { bg: "bg-rose-500/15", text: "text-rose-500", dot: "bg-rose-500" },
  unknown: { bg: "bg-slate-500/10", text: "text-slate-400", dot: "bg-slate-400" },
};

const fmtDuration = (s) => {
  if (s == null) return "—";
  if (s < 1) return `${(s * 1000).toFixed(0)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${m}m ${rem}s`;
};

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const statusBadge = (status, dm) => {
  const map = {
    completed: { bg: "bg-emerald-500/10", text: "text-emerald-500", label: "completed" },
    running: { bg: "bg-blue-500/10", text: "text-blue-500", label: "running" },
    failed: { bg: "bg-rose-500/10", text: "text-rose-500", label: "failed" },
    timeout: { bg: "bg-rose-500/10", text: "text-rose-500", label: "timeout" },
    killed_stale: { bg: "bg-slate-500/10", text: "text-slate-400", label: "killed_stale" },
  };
  const s = map[status] || { bg: "bg-slate-500/10", text: "text-slate-400", label: status };
  return (
    <span
      data-testid={`run-status-${status}`}
      className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
};

export default function ScrapePipelineHealthWidget({ dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTs, setRefreshTs] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await axios.get(`${API}/api/admin/scrape-pipeline-health?limit=10`, {
        withCredentials: true,
      });
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30s while the tab is visible
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData, refreshTs]);

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardStyle = {
    boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)",
  };
  const heading = `editorial-heading text-xl font-medium mb-5 ${
    dm ? "text-slate-100" : "text-[#191c1d]"
  }`;
  const sub = `category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`;

  return (
    <div className={card} style={cardStyle} data-testid="scrape-pipeline-health-widget">
      <div className="flex items-baseline justify-between mb-5">
        <h3 className={heading}>Scrape Pipeline Health</h3>
        <button
          data-testid="refresh-pipeline-health"
          onClick={() => setRefreshTs(Date.now())}
          className={`text-xs underline ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}
        >
          Refresh
        </button>
      </div>

      {loading && !data && (
        <p className={`text-sm ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Loading…</p>
      )}

      {error && (
        <p data-testid="pipeline-health-error" className="text-sm text-rose-500">
          {error}
        </p>
      )}

      {data && (
        <>
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Completed", value: data.summary.completed_count, color: "text-emerald-500" },
              { label: "Running", value: data.summary.running_count, color: "text-blue-500" },
              { label: "Failed/Timeout", value: data.summary.failed_count, color: "text-rose-500" },
              { label: "Killed stale", value: data.summary.killed_stale_count, color: "text-slate-400" },
            ].map((kpi) => (
              <div
                key={kpi.label}
                className={`p-3 rounded-lg ${dm ? "bg-white/5" : "bg-black/4"}`}
              >
                <p className={sub}>{kpi.label}</p>
                <p className={`text-2xl font-medium mt-1 ${kpi.color}`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Median per-stage timing (across completed runs) */}
          {data.median_durations_s && (
            <div className="mb-6">
              <p className={`${sub} mb-2`}>Median stage durations (last {data.summary.completed_count} completed runs)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { key: "cleanup", label: "Cleanup" },
                  { key: "discovery_and_summarize", label: "Discovery + AI" },
                  { key: "persist", label: "Persist" },
                ].map(({ key, label }) => {
                  const med = data.median_durations_s[key];
                  const threshold = data.thresholds_s?.[key];
                  let band = "unknown";
                  if (med != null && threshold != null) {
                    if (med <= threshold) band = "green";
                    else if (med <= threshold * 2) band = "yellow";
                    else band = "red";
                  }
                  const c = BAND_COLORS[band];
                  return (
                    <div
                      key={key}
                      data-testid={`median-${key}`}
                      className={`p-3 rounded-lg ${c.bg} flex items-center justify-between`}
                    >
                      <div>
                        <p className={sub}>{label}</p>
                        <p className={`text-base font-medium mt-0.5 ${c.text}`}>
                          {fmtDuration(med)}
                        </p>
                      </div>
                      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-run timeline */}
          <p className={`${sub} mb-2`}>Last {data.runs.length} runs (newest first)</p>
          <div className="space-y-2">
            {data.runs.map((run) => (
              <div
                key={run.id}
                data-testid={`pipeline-run-${run.id}`}
                className={`p-3 rounded-lg ${dm ? "bg-white/5" : "bg-black/4"}`}
              >
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {statusBadge(run.status, dm)}
                    <span className={`text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                      {fmtTime(run.startedAt)}
                    </span>
                    {run.articlesFound > 0 && (
                      <span className={`text-xs ${dm ? "text-emerald-500" : "text-emerald-600"}`}>
                        +{run.articlesFound} articles
                      </span>
                    )}
                  </div>
                </div>

                {/* Per-stage bands */}
                <div className="grid grid-cols-3 gap-1.5">
                  {run.stages.map((s) => {
                    const c = BAND_COLORS[s.band];
                    return (
                      <div
                        key={s.key}
                        data-testid={`run-stage-${run.id}-${s.key}`}
                        className={`px-2 py-1.5 rounded ${c.bg} flex items-center justify-between gap-2 min-w-0`}
                      >
                        <span className={`text-[10px] truncate ${c.text}`}>{s.label}</span>
                        <span className={`text-xs font-medium tabular-nums ${c.text}`}>
                          {fmtDuration(s.duration_s)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {run.errors && run.errors.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {run.errors.map((err, i) => (
                      <p
                        key={i}
                        className="text-[10px] text-rose-500 truncate"
                        title={err}
                      >
                        ⚠ {err}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer: thresholds key */}
          <div className={`mt-5 pt-4 border-t ${dm ? "border-white/4" : "border-black/4"}`}>
            <p className={sub}>
              Thresholds: green ≤ N, yellow ≤ 2N, red &gt; 2N. Per stage (N): cleanup{" "}
              {data.thresholds_s?.cleanup}s · discovery+AI{" "}
              {Math.floor((data.thresholds_s?.discovery_and_summarize ?? 0) / 60)}min · persist{" "}
              {data.thresholds_s?.persist}s
            </p>
          </div>
        </>
      )}
    </div>
  );
}
